import {
  AddComment20,
  ArrowLeft20,
  Chat20,
  CheckmarkOutline20,
  Edit16,
  Stamp20,
} from '@carbon/icons-react';
import { Tooltip } from 'antd';
import moment from 'moment';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import styled, { css } from 'styled-components';
import { ConversationAPI } from 'api/conversations';
import { UserType } from 'api/models';
import { PinResourceType, PinScopeType } from 'api/spotlight/types';
import { ReactComponent as Freeze } from 'assets/svgs/freeze.svg';
import {
  CpBody01Primary,
  CpBody01Secondary,
  CpBody0aSecondary,
} from 'components/atoms/customer-portal/typography';
import { humanise } from 'components/atoms/humanised-date';
import { Iframely as IframelyAtom } from 'components/atoms/iframely';
import { Loading } from 'components/atoms/loading';
import { ConversationPane } from 'components/organisms/conversations';
import { MessagesComment } from 'components/organisms/conversations/messages-comment';
import {
  FreezeAction,
  TabNames,
} from 'components/organisms/document/document.types';
import { CurrentSpaceContext } from 'components/organisms/spaces/context';
import { RenderRocketlaneDocument } from 'components/organisms/spaces/space-tab/space-tab';
import { useSpaceActions } from 'components/pages/project-details/project-spaces/use-space-actions';
import { useProjectDetail } from 'data/projects';
import { useGetUserFromIndexedDb } from 'hooks/use-idb';
import { useGetSpotlights } from 'hooks/use-spotlight';
import { useUser } from 'hooks/use-user';
import { useAccountDataQuery } from 'queries/account';
import { useDocumentApprovalsQuery } from 'queries/document-approvals';
import { useSpaceTabQuery } from 'queries/project-spaces';
import { CpPopover } from '../../components/modal/cp-popover';
import { useCustomerPortalHashMap } from '../../utils';
import { CpDocumentCommentsPane } from './cp-document-comments-pane';
import { CpDocumentsTaskPane } from './cp-documents-task-pane';
import { CpSpaceTabApprovalsPane } from './cp-space-tab-approvals-pane';

const SecondaryTitleBar = styled.div`
  padding: var(--cp-spacing-04) var(--cp-spacing-05);
  margin-top: var(--cp-spacing-10);
  background: var(--blue-10);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--cp-spacing-03);
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

const TitleBar = styled.div`
  width: 100%;
  /* background: black; */
  background: var(--cp-black-opacity-80-hover);
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--cp-spacing-05);
  position: fixed;
  top: 0;
  left: 0;
  padding-left: var(--cp-spacing-06);
  padding-right: var(--cp-spacing-06);
  padding-top: var(--cp-spacing-04);
  padding-bottom: var(--cp-spacing-04);
  --cp-text-primary: var(--cp-white-100);
  --cp-text-secondary: var(--cp-white-100);
  p {
    margin-bottom: 0;
  }
  svg {
    fill: var(--cp-white-100);
  }
`;

const BackWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  svg {
    margin-right: var(--cp-spacing-03);
  }
  max-width: 400px;
  @media (max-width: 480px) {
    max-width: 150px;
  }
`;

const IconWrapper = styled.div`
  padding: var(--cp-spacing-03);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const EditWrapper = styled.div`
  padding-top: var(--cp-spacing-03);
  padding-bottom: var(--cp-spacing-03);
  padding-left: var(--cp-spacing-05);
  padding-right: var(--cp-spacing-05);
  display: none;
  align-items: center;
  justify-content: center;
  svg {
    margin-right: var(--cp-spacing-03);
  }
  border-radius: 2rem;
  cursor: pointer;
  border: 1px solid var(--cp-white-100);

  @media (min-width: 1080px) {
    display: flex;
  }
`;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--cp-spacing-03);
`;

const Content = styled.div`
  height: 100vh;
  width: 100%;
  display: flex;
  background: #003147;
  padding-top: var(--cp-spacing-10);
  .ant-tabs,
  [data-cy='spaces-toolbar'],
  .document-tab-container,
  .distraction-free-mode-btn {
    display: none;
  }

  [data-test-id='space.tab'] {
    [class*='document_document--ready'] {
      height: 100%;
    }
  }

  @media (min-width: 1080px) {
    padding-left: max(var(--spacing-10), calc(calc(100% - 1080px) / 2));
    padding-right: max(var(--spacing-10), calc(calc(100% - 1080px) / 2));
  }
`;
const Page = styled.div<{ hasSecondaryTitlebar: boolean }>`
  height: 100%;
  width: 100%;

  ${({ hasSecondaryTitlebar }) =>
    hasSecondaryTitlebar
      ? css`
          @media (min-width: 1080px) {
            ${SecondaryTitleBar} {
              display: none;
            }
          }
          @media (max-width: 1079px) {
            ${SecondaryTitleBar} {
              display: flex;
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 48px;
            }
            ${Content} {
              padding-top: calc(var(--cp-spacing-10) + 48px);
            }
          }
        `
      : ''};
`;

const MessagesPopover = styled(CpPopover)`
  @media (min-width: 481px) {
    &.ant-popover {
      height: 800px;
      width: 350px;
      max-height: calc(100vh - 60px);
    }
    .ant-popover-content,
    .ant-popover-inner,
    .ant-popover-inner-content {
      height: 100%;
    }
  }
`;

const ApprovalsPopopver = styled(CpPopover)`
  @media (min-width: 481px) {
    &.ant-popover {
      height: 800px;
      width: 400px;
      max-height: calc(100vh - 60px);
    }
    .ant-popover-content,
    .ant-popover-inner,
    .ant-popover-inner-content {
      height: 100%;
    }
  }
`;

export const Iframely = styled(IframelyAtom)`
  overflow: auto;
  iframe {
    height: 100vh !important;
  }
  position: relative;
  flex-grow: 1;
  transition: flex-basis var(--animation-duration-slow);
`;

const Title = styled(CpBody01Primary)`
  max-width: 400px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

export const CpSpaceTabPreview: React.FC<{
  spaceTabId: number;
  spaceId: number;
  projectId: number;
}> = ({ spaceTabId, projectId, spaceId }) => {
  const router = useHistory();
  const currentUser = useUser();
  const { data: accountData } = useAccountDataQuery();
  const { data: project } = useProjectDetail(['project_id', projectId]);
  const { data: spaceTab } = useSpaceTabQuery(spaceTabId);
  const [isEditing, setIsEditing] = useState(false);

  const { currentSpaceDetails } = useSpaceActions({
    spaceId: spaceId?.toString() ?? '',
    projectId,
    spaceTabId: spaceTabId?.toString() ?? '',
  });
  const { hashMap, removeKey } = useCustomerPortalHashMap();
  const [isApprovalsOpen, setIsApprovalsOpen] = useState(
    () => hashMap?.st_approvals === 'open',
  );
  const [isConversationsOpen, setIsConversationsOpen] = useState(false);
  const [freezeMode, setFreezeMode] = useState<FreezeAction>({
    frozen: false,
  });

  const frozenByUser = useGetUserFromIndexedDb(freezeMode?.frozenBy);
  const onFreezeModeChange = useCallback((next) => {
    setFreezeMode(next);
  }, []);
  const onSpotlightClick = useCallback((pin) => {
    setIsConversationsOpen(true);
    setTimeout(() => {
      document
        .querySelector(`[data-spotlight='${pin?.resourceId}']`)
        ?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
    }, 300);
  }, []);
  const documentId = useMemo(() => {
    const split = spaceTab?.url?.split('/')?.[3];

    if (!split) {
      return 0;
    }

    return parseInt(split, 10);
  }, [spaceTab?.url]);
  const { data: spotlights = [] } = useGetSpotlights({
    resourceType: PinResourceType.Chat,
    scopeId: documentId,
    scopeType: PinScopeType.Document,
  });

  const { data: approvals = [] } = useDocumentApprovalsQuery(
    ['document_id', documentId],
    ['project_id', projectId],
  );
  const backRef = useRef<boolean | null>(null);
  const docType: 'ROCKETLANE_DOCUMENT' | 'GOOGLE_DOCUMENT' | undefined =
    useMemo(() => {
      return spaceTab?.spaceTabType;
    }, [spaceTab]);

  const onBack = useCallback(() => {
    // go back if history is available
    if (router.length > 1) {
      try {
        router.goBack();
        backRef.current = true;
      } catch (e) {
        if (!backRef.current) {
          onBack();
        }
      }

      return;
    }
    // else go to root
    router.push('/');
  }, [router]);

  useEffect(() => {
    if (hashMap?.st_approvals === 'open') {
      setIsApprovalsOpen(true);
    } else {
      setIsApprovalsOpen(false);
    }
  }, [hashMap?.st_approvals]);

  const renderMessagesComponent = useCallback(() => {
    if (
      currentUser?.user?.userType === UserType.CustomerPortalBot &&
      projectId
    ) {
      const accountId = accountData?.account?.accountId;

      if (!accountId) {
        return undefined;
      }

      return (
        <MessagesComment
          actionsOnLeft
          messageContent=""
          placeholder=""
          hidePrivate
          readonly
          handleUserMention={() => {}}
          accountId={accountId}
          projectId={projectId}
          onAddComment={() => {}}
          onClearReply={() => {}}
          hasAccessToCreatePrivateCommentsOnly={false}
        />
      );
    }

    return undefined;
  }, [accountData?.account?.accountId, currentUser?.user?.userType, projectId]);

  const editor = useMemo(() => {
    if (!spaceTab || !currentSpaceDetails) {
      return (
        <LoadingWrapper>
          <Loading size="large" />
        </LoadingWrapper>
      );
    }

    if (!isEditing) {
      return (
        <RenderRocketlaneDocument
          readonly
          data={{
            ...spaceTab,
            space: spaceTab?.space?.spaceId,
            taskList: spaceTab?.taskList?.map?.((e) => e?.taskId) ?? [],
          }}
          onFrozenStateChange={onFreezeModeChange}
          onSpotlightClick={onSpotlightClick}
        />
      );
    }

    return (
      <RenderRocketlaneDocument
        data={{
          ...spaceTab,
          space: spaceTab?.space?.spaceId,
          taskList: spaceTab?.taskList?.map?.((e) => e?.taskId) ?? [],
        }}
        onFrozenStateChange={onFreezeModeChange}
        onSpotlightClick={onSpotlightClick}
      />
    );
  }, [
    currentSpaceDetails,
    isEditing,
    onFreezeModeChange,
    onSpotlightClick,
    spaceTab,
  ]);

  if (!spaceTab || !currentSpaceDetails) {
    return (
      <LoadingWrapper>
        <Loading size="large" />
      </LoadingWrapper>
    );
  }

  return (
    <Page hasSecondaryTitlebar={freezeMode?.frozen === true}>
      {spaceTab?.spaceTabTitle ? (
        <Helmet>
          <title>{spaceTab?.spaceTabTitle}</title>
        </Helmet>
      ) : (
        <></>
      )}
      <TitleBar>
        <BackWrapper onClick={onBack}>
          <IconWrapper>
            <ArrowLeft20 />
          </IconWrapper>
          <Title>{spaceTab?.spaceTabTitle}</Title>
        </BackWrapper>
        {freezeMode?.frozen === true ? (
          <EditWrapper>
            <Freeze color="var(--cp-white)" />
            <Tooltip
              title={
                <FormattedMessage
                  id="document.freeze_by_exact"
                  values={{
                    time: moment(freezeMode?.frozenAt).calendar(),
                  }}
                />
              }
              placement="bottom"
            >
              <CpBody01Secondary>
                <FormattedMessage
                  id="document.freeze_by"
                  values={{
                    user: frozenByUser?.firstName,
                    time: humanise(moment(freezeMode?.frozenAt)),
                  }}
                />
              </CpBody01Secondary>
            </Tooltip>
          </EditWrapper>
        ) : isEditing ? (
          <></>
        ) : (
          <EditWrapper
            onClick={() => {
              setIsEditing((prev) => !prev);
            }}
          >
            <Edit16 />
            <CpBody01Secondary>
              <FormattedMessage id="customer_portal.file_preview.edit_the_document" />
            </CpBody01Secondary>
          </EditWrapper>
        )}
        <ActionsWrapper>
          <MessagesPopover
            visible={isConversationsOpen}
            onVisibleChange={(visible) => {
              setIsConversationsOpen(visible);
            }}
            trigger={['click']}
            content={
              <ConversationPane
                messagesComponent={renderMessagesComponent()}
                accountId={project?.accountId as number}
                apiService={ConversationAPI}
                context={
                  spaceTab?.spaceTabType === 'ROCKETLANE_DOCUMENT'
                    ? {
                        id: parseInt(spaceTab?.url?.split('/')?.[3], 10),
                        type: 'DOCUMENT',
                      }
                    : {
                        id: spaceTabId,
                        type: 'SPACE_TAB',
                      }
                }
                projectId={projectId}
                spotlights={spotlights}
                hideAuditLog
                commentBoxConfig={{
                  hidePrivate: true,
                  hideSmartfills: true,
                }}
              />
            }
          >
            <Tooltip
              title={<FormattedMessage id={TabNames.ConversationsTab} />}
            >
              <IconWrapper>
                <Chat20 />
              </IconWrapper>
            </Tooltip>
          </MessagesPopover>

          {spaceTab?.spaceTabType ===
          // Hardcoding because of import issue
          'ROCKETLANE_DOCUMENT' ? (
            <>
              <MessagesPopover
                trigger={['click']}
                content={
                  <CpDocumentsTaskPane
                    spaceTabId={spaceTabId}
                    projectId={projectId}
                  />
                }
              >
                <Tooltip title={<FormattedMessage id={TabNames.Tasks} />}>
                  <IconWrapper>
                    <CheckmarkOutline20 />
                  </IconWrapper>
                </Tooltip>
              </MessagesPopover>
              <MessagesPopover
                trigger={['click']}
                content={
                  <CpDocumentCommentsPane
                    spaceTabId={spaceTabId}
                    projectId={projectId}
                  />
                }
              >
                <Tooltip title={<FormattedMessage id={TabNames.CommentsTab} />}>
                  <IconWrapper>
                    <AddComment20 />
                  </IconWrapper>
                </Tooltip>
              </MessagesPopover>
            </>
          ) : (
            <></>
          )}

          {spaceTab?.spaceTabType === 'ROCKETLANE_DOCUMENT' &&
          approvals?.length ? (
            <ApprovalsPopopver
              visible={isApprovalsOpen}
              onVisibleChange={(visible) => {
                setIsApprovalsOpen(visible);
                removeKey('st_approvals');
              }}
              trigger={['click']}
              content={
                <CpSpaceTabApprovalsPane
                  documentId={parseInt(spaceTab?.url?.split('/')?.[3], 10)}
                  projectId={projectId}
                />
              }
            >
              <Tooltip title={<FormattedMessage id={TabNames.Approvals} />}>
                <IconWrapper>
                  <Stamp20 />
                </IconWrapper>
              </Tooltip>
            </ApprovalsPopopver>
          ) : (
            <></>
          )}
          {/* Removing because of no UI in figma */}
          {/* <IconWrapper>
            <OverflowMenuVertical20 />
          </IconWrapper> */}
        </ActionsWrapper>
      </TitleBar>
      {freezeMode.frozen === true ? (
        <SecondaryTitleBar>
          <Freeze />
          <CpBody0aSecondary>
            <FormattedMessage
              id="document.freeze_by"
              values={{
                user: frozenByUser?.firstName,
                time: humanise(moment(freezeMode?.frozenAt)),
              }}
            />
          </CpBody0aSecondary>
        </SecondaryTitleBar>
      ) : (
        <></>
      )}
      <Content>
        {docType === 'ROCKETLANE_DOCUMENT' ? (
          <CurrentSpaceContext.Provider value={currentSpaceDetails}>
            {editor}
          </CurrentSpaceContext.Provider>
        ) : spaceTab?.metaData?.html ? (
          <Iframely html={spaceTab?.metaData?.html} />
        ) : (
          <></>
        )}
      </Content>
    </Page>
  );
};
